import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const GoogleIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"/></svg>);
const DiscordIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="#5865F2" aria-hidden="true"><path d="M20.3 4.4A19.8 19.8 0 0 0 15.4 3l-.24.5a18 18 0 0 1 4.3 1.4A13.6 13.6 0 0 0 4.5 4.9 18 18 0 0 1 8.8 3.5L8.6 3a19.8 19.8 0 0 0-4.9 1.4C1 8.7.3 12.8.6 16.9a20 20 0 0 0 6 3l.8-1.1a13 13 0 0 1-2-1l.5-.4a14 14 0 0 0 12 0l.5.4c-.6.4-1.3.7-2 1l.8 1.1a20 20 0 0 0 6-3c.4-4.8-.6-8.9-3.6-12.5zM9 14.3c-1.2 0-2.1-1.1-2.1-2.4C6.9 10.6 7.8 9.5 9 9.5s2.1 1.1 2.1 2.4c0 1.3-.9 2.4-2.1 2.4zm6 0c-1.2 0-2.1-1.1-2.1-2.4 0-1.3.9-2.4 2.1-2.4s2.1 1.1 2.1 2.4c0 1.3-.9 2.4-2.1 2.4z"/></svg>);

export function socialLogin() {
  return null;
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      nav("/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const discord = async () => {
    try {
      const { data } = await api.get("/auth/discord/login");
      window.location.href = data.url;
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Discord login unavailable");
    }
  };

  return (
    <div className="min-h-screen grid-bg grid place-items-center px-6 py-16">
      <div className="w-full max-w-md fade-up">
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-8" data-testid="auth-logo">
          <img src="/logo.png" alt="FR Proxies" className="w-10 h-10 rounded-lg object-contain" />
          <span className="font-head font-extrabold tracking-tight text-xl">FR <span className="text-neon">PROXIES</span></span>
        </Link>
        <div className="led-border bg-surface border border-[#CBEEF5] rounded-xl p-8 glow-cyan">
          <h1 className="font-head text-2xl font-bold mb-1">Welcome back</h1>
          <p className="text-sm text-zinc-500 mb-6">Sign in to manage your proxies.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-zinc-400">Email</Label>
              <Input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 bg-base border-[#CBEEF5] focus:border-neon" placeholder="you@example.com" />
            </div>
            <div>
              <Label className="text-zinc-400">Password</Label>
              <Input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 bg-base border-[#CBEEF5] focus:border-neon" placeholder="••••••••" />
            </div>
            <Button data-testid="login-submit-button" disabled={busy} className="w-full bg-neon text-base hover:bg-neon-hover font-semibold">{busy ? "Signing in…" : "Sign in"}</Button>
          </form>
          <div className="flex items-center gap-3 my-6"><div className="h-px bg-[#262626] flex-1" /><span className="text-xs text-zinc-600 font-mono">OR</span><div className="h-px bg-[#262626] flex-1" /></div>
          <div className="space-y-3">
            <button onClick={google} data-testid="google-login-button" className="w-full border border-[#CBEEF5] bg-base hover:bg-surface2 flex items-center justify-center gap-3 py-3 rounded-md transition-colors text-sm"><GoogleIcon /> Continue with Google</button>
            <button onClick={discord} data-testid="discord-login-button" className="w-full border border-[#CBEEF5] bg-base hover:bg-surface2 flex items-center justify-center gap-3 py-3 rounded-md transition-colors text-sm"><DiscordIcon /> Continue with Discord</button>
          </div>
          <p className="text-sm text-zinc-500 text-center mt-6">No account? <Link to="/register" className="text-neon hover:underline" data-testid="goto-register-link">Create one</Link></p>
        </div>
      </div>
    </div>
  );
}
